import React, { useRef, useState } from 'react';
import axios from 'axios';
import { SystemEntityPathOptionsState } from '../../hooks/useSystemEntityPathOptions';
import { useConfirm } from '../../context/ConfirmContext';
import ReassignDevicesDialog from './ReassignDevicesDialog';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { EntityPathOption } from '../../types';
import Button from '../ui/Button';
import ActionRow from '../ui/ActionRow';
import { Input } from '../pilot-ui/input';
import { Label } from '../pilot-ui/label';

interface ReassignState {
  path: string;
  count: number;
  submitting: boolean;
  error: string | null;
}

interface EntityPathOptionsPanelProps {
  entityPathOptions: SystemEntityPathOptionsState;
}

// FS-910: the 409 body of a refused delete. `code` says why:
// entity_path_in_use (devices on exactly this path), entity_path_is_default,
// or entity_path_would_orphan_devices (devices under it that no other option
// covers).
interface DeleteConflict {
  code?: string;
  count?: number;
  message?: string;
}

const deleteConflict = (err: unknown): DeleteConflict | null => {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) return null;
  const data = err.response.data;
  return data && typeof data === 'object' ? (data as DeleteConflict) : null;
};

// Why a delete did not happen, for the page.
const refusalText = (path: string, err: unknown): string => {
  const conflict = deleteConflict(err);
  if (conflict?.code === 'entity_path_would_orphan_devices' && conflict.message) {
    return `${path} was not deleted: ${conflict.message} Add an option that covers those devices, or change their paths, first.`;
  }
  return `${path} was not deleted: ${conflict?.message ?? extractErrorMessage(err, 'Delete failed')}`;
};

// Plan 83: CRUD UI for entity_path_options. Mounted on /system page; the
// page itself is gated by system_admin via RequirePermission in App.tsx.
const EntityPathOptionsPanel: React.FC<EntityPathOptionsPanelProps> = ({ entityPathOptions }) => {
  const { options, loading, error, create, updateLabel, remove, reassign } =
    entityPathOptions;
  const confirm = useConfirm();
  const [reassignState, setReassignState] = useState<ReassignState | null>(null);
  const newPathInput = useRef<HTMLInputElement>(null);
  const deleteTrigger = useRef<HTMLButtonElement>(null);
  const editFocusTarget = useRef<string | null>(null);

  const [newPath, setNewPath] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // What the last action committed, when it is worth saying (a move).
  const [notice, setNotice] = useState<string | null>(null);

  // Per-row label edits — keyed by path so multiple rows can stay in edit
  // mode at once if the operator wants.
  const [labelDrafts, setLabelDrafts] = useState<Record<string, string>>({});

  const setDraft = (path: string, label: string) =>
    setLabelDrafts((prev) => ({ ...prev, [path]: label }));

  const clearDraft = (path: string) =>
    setLabelDrafts((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });

  const cancelDraft = (path: string) => {
    editFocusTarget.current = path;
    clearDraft(path);
  };

  // Saved trimmed, empty as no label: a draft that comes out the same as the
  // stored label is nothing to save.
  const labelChanged = (opt: EntityPathOption) => {
    const draft = labelDrafts[opt.path];
    return draft !== undefined && (draft.trim() || null) !== (opt.label || null);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newPath.trim();
    if (!trimmed) return;
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      await create(trimmed, newLabel.trim() || null);
      setNewPath('');
      setNewLabel('');
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Add failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveLabel = async (opt: EntityPathOption) => {
    if (!labelChanged(opt)) return;
    const { path } = opt;
    const draft = labelDrafts[path] ?? '';
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      await updateLabel(path, draft.trim() || null);
      clearDraft(path);
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Update failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (path: string) => {
    const ok = await confirm({
      title: 'Delete entity path?',
      message: (
        <span>
          Delete <span className="font-mono">{path}</span>? Devices already on
          this path will need to be moved first; you'll be prompted if so.
        </span>
      ),
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;

    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      await remove(path);
      // The row trigger disappears after refresh, so restore focus after React removes it.
      window.requestAnimationFrame(() => newPathInput.current?.focus());
    } catch (err) {
      // FS-910: only devices on exactly this path can be moved from here, so
      // only that refusal opens the move form. The default and devices on
      // sub-paths are shown on the page and left to the operator; a move
      // would not make those deletes succeed.
      const conflict = deleteConflict(err);
      if (conflict?.code === 'entity_path_in_use' && typeof conflict.count === 'number' && conflict.count > 0) {
        setReassignState({ path, count: conflict.count, submitting: false, error: null });
        return;
      }
      setActionError(refusalText(path, err));
    } finally {
      setBusy(false);
    }
  };

  const handleReassignConfirm = async (target: string) => {
    if (!reassignState) return;
    const { path } = reassignState;
    setReassignState({ ...reassignState, submitting: true, error: null });
    let moved: number;
    try {
      ({ moved } = await reassign(path, target));
    } catch (err) {
      // Nothing moved: the form stays open with the reason.
      setReassignState((prev) =>
        prev
          ? { ...prev, submitting: false, error: extractErrorMessage(err, 'Move failed') }
          : prev,
      );
      return;
    }
    // FS-910: the move has committed whatever the delete does next, so the
    // page reports the two apart.
    const movedText = `Moved ${moved} device(s) with path exactly ${path} to ${target}.`;
    try {
      await remove(path);
      setNotice(`${movedText} Deleted ${path}.`);
    } catch (err) {
      setNotice(movedText);
      setActionError(refusalText(path, err));
    }
    setReassignState(null);
    window.requestAnimationFrame(() => newPathInput.current?.focus());
  };

  return (
    <div>
      {loading && <p className="text-muted-foreground">Loading...</p>}
      {error && <p className="text-status-error-foreground text-sm mb-2">{error}</p>}
      {notice && (
        <div role="status" className="mb-3 p-2 border border-status-success-border bg-green-50 text-sm text-green-800 rounded-sm">
          {notice}
        </div>
      )}
      {actionError && (
        <div role="alert" className="mb-3 p-2 border border-status-error-border bg-red-50 text-sm text-red-800 rounded-sm">
          {actionError}
        </div>
      )}

      <div className="max-w-full overflow-x-auto mb-4">
        <table className="table-auto w-full text-left border-collapse border border-border text-sm">
          <thead>
            <tr className="bg-gray-200">
              <th className="px-3 py-2 border">Path</th>
              <th className="px-3 py-2 border">Label</th>
              <th className="px-3 py-2 border">Created</th>
              <th className="px-3 py-2 border w-48">Actions</th>
            </tr>
          </thead>
          <tbody>
            {options.map((opt) => {
              const editing = labelDrafts[opt.path] !== undefined;
              const changed = labelChanged(opt);
              return (
                <tr key={opt.path} className="hover:bg-gray-100">
                  <td className="px-3 py-2 border font-mono text-xs">{opt.path}</td>
                  <td className="px-3 py-2 border">
                    {editing ? (
                      <div>
                        <Label htmlFor={`label-edit-${opt.path}`} className="block text-xs">Label for {opt.path}</Label>
                        <Input
                          id={`label-edit-${opt.path}`}
                          type="text"
                          aria-label={`Label for ${opt.path}`}
                          aria-describedby={`label-help-${opt.path}`}
                          title="Change the readable label for this path; the path itself stays the same."
                          value={labelDrafts[opt.path] ?? ''}
                          onChange={(e) => setDraft(opt.path, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelDraft(opt.path);
                            } else if (e.key === 'Enter') {
                              e.preventDefault();
                              if (changed && !busy) handleSaveLabel(opt);
                            }
                          }}
                          className="w-full px-2 py-1 border rounded-sm text-sm"
                          autoFocus
                        />
                      </div>
                    ) : (
                      opt.label || <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 border text-xs text-gray-600">
                    {opt.created_at
                      ? new Date(opt.created_at).toISOString().slice(0, 10)
                      : '—'}
                  </td>
                  <td className="px-3 py-2 border">
                    {editing ? (
                      <div>
                        <ActionRow
                          reason={!changed && 'No changes to save.'}
                          reasonId={`label-disabled-${opt.path}`}
                        >
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={busy || !changed}
                            aria-describedby={!changed ? `label-disabled-${opt.path}` : undefined}
                            onClick={() => handleSaveLabel(opt)}
                            title={changed
                              ? 'Save the new label for this entity path (Enter does the same)'
                              : 'Nothing to save: the label is unchanged'}
                          >
                            Save
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={busy}
                            onClick={() => cancelDraft(opt.path)}
                            title="Stop editing and keep the label as it was (Escape does the same)"
                          >
                            Cancel
                          </Button>
                        </ActionRow>
                        <span id={`label-help-${opt.path}`} className="block text-xs text-gray-700">Save updates this path label.</span>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          ref={node => {
                            if (node && editFocusTarget.current === opt.path) {
                              node.focus();
                              editFocusTarget.current = null;
                            }
                          }}
                          disabled={busy}
                          onClick={() => setDraft(opt.path, opt.label ?? '')}
                          title="Change the readable name shown for this entity path; the path itself stays the same"
                        >
                          Edit Label
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={busy}
                          onClick={(event) => {
                            deleteTrigger.current = event.currentTarget;
                            handleDelete(opt.path);
                          }}
                          title="Delete this entity path option; if devices are on exactly this path you are asked where to move them first"
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {options.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className="px-3 py-2 border text-center text-muted-foreground">
                  No entity path options yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <form onSubmit={handleAdd} className="flex flex-wrap gap-2 items-end">
        <div>
          <Label htmlFor="new-entity-path" className="mb-1 text-xs text-gray-700">
            New Path
          </Label>
          <p id="new-path-help" className="text-xs text-muted-foreground">Must start and end with /; adds a path operators can assign to devices.</p>
          <Input
            id="new-entity-path"
            ref={newPathInput}
            type="text"
            aria-describedby="new-path-help"
            value={newPath}
            onChange={(e) => setNewPath(e.target.value)}
            placeholder="/companyx/west/"
            pattern="^/(?:[^/]+/)*$"
            title="Path must start and end with '/' (e.g. /companyx/west/)"
            className="px-2 py-1 border rounded-sm text-sm font-mono w-72"
            required
          />
        </div>
        <div>
          <Label htmlFor="new-entity-label" className="mb-1 text-xs text-gray-700">
            Label (optional)
          </Label>
          <p id="new-label-help" className="text-xs text-muted-foreground">Shown in device selectors to help operators recognize this path.</p>
          <Input
            id="new-entity-label"
            type="text"
            aria-describedby="new-label-help"
            title="A readable name to help operators recognize this path in device selectors."
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Company X — West"
            className="px-2 py-1 border rounded-sm text-sm w-72"
          />
        </div>
        <Button
          variant="primary"
          type="submit"
          disabled={busy || !newPath.trim()}
          title="Add this entity path to the list operators can assign devices to"
        >
          Add
        </Button>
      </form>

      <ReassignDevicesDialog
        open={!!reassignState}
        sourcePath={reassignState?.path ?? ''}
        deviceCount={reassignState?.count ?? 0}
        options={options}
        submitting={reassignState?.submitting}
        error={reassignState?.error ?? null}
        onCancel={() => {
          setReassignState(null);
          window.requestAnimationFrame(() => {
            const target = deleteTrigger.current?.isConnected ? deleteTrigger.current : newPathInput.current;
            target?.focus();
          });
        }}
        onConfirm={handleReassignConfirm}
      />
    </div>
  );
};

export default EntityPathOptionsPanel;
